import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QuizPage from "../../quiz/QuizPage";

const resolveItemFromResponse = (response) => {
  if (!response) {
    return null;
  }

  if (response.item) {
    return response.item;
  }

  if (Array.isArray(response.items)) {
    return response.items[0] || null;
  }

  return response;
};

const DEFAULT_OPTION_COUNT = 2;

const createDefaultJsonTemplate = () => ({
  Type: "MCMA",
  Title: "",
  Options: [],
});

const createEmptyItem = () => ({
  Type: "MCMA",
  Name: "",
  Title: "",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: Array.from({ length: DEFAULT_OPTION_COUNT }, (_, index) => ({
    id: index + 1,
    Value: "",
    IsCorrect: false,
    Rationale: "",
  })),
});

const cloneJsonValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneJsonValue(entry));
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = cloneJsonValue(value[key]);
      return acc;
    }, {});
  }

  return value;
};

const parseJsonTemplate = (item) => {
  if (!item || typeof item !== "object") {
    return { template: null, keys: [], serializeMap: {} };
  }

  const keys = [];
  const serializeMap = {};
  let selectedTemplate = null;

  const parseCandidate = (candidate) => {
    if (typeof candidate === "string") {
      try {
        return { template: JSON.parse(candidate), serialize: true };
      } catch {
        return { template: null, serialize: true };
      }
    }

    if (candidate && typeof candidate === "object") {
      return { template: candidate, serialize: false };
    }

    return { template: null, serialize: false };
  };

  ["json", "Json"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      const { template, serialize } = parseCandidate(item[key]);
      keys.push(key);
      serializeMap[key] = serialize;

      if (!selectedTemplate && template) {
        selectedTemplate = template;
      }
    }
  });

  return { template: selectedTemplate, keys, serializeMap };
};

const normalisePointsValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : value;
};

const normaliseOptionsList = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map((option, index) => ({
    id: option?.id ?? index + 1,
    Value: option?.Value ?? "",
    IsCorrect: Boolean(option?.IsCorrect),
    Rationale: option?.Rationale ?? "",
  }));
};

const applyItemToJsonTemplate = (template, itemState, options, points) => {
  const base =
    template && typeof template === "object" ? cloneJsonValue(template) : null;

  const titleValue =
    itemState?.Title !== undefined && itemState?.Title !== null
      ? itemState.Title
      : "";
  const normalizedSkilltype = normalizeSkillTypeValue(itemState?.skilltype);

  if (base && typeof base === "object" && !Array.isArray(base)) {
    let handledOptions = false;

    if (
      Object.prototype.hasOwnProperty.call(base, "Options") ||
      Array.isArray(base.Options)
    ) {
      base.Options = options;
      handledOptions = true;
    }

    if (
      base.Question &&
      typeof base.Question === "object" &&
      !Array.isArray(base.Question)
    ) {
      const questionClone = cloneJsonValue(base.Question);

      if (
        Object.prototype.hasOwnProperty.call(questionClone, "Options") ||
        Array.isArray(questionClone.Options)
      ) {
        questionClone.Options = options;
        handledOptions = true;
      }

      if (titleValue !== undefined) {
        if (Object.prototype.hasOwnProperty.call(questionClone, "Title")) {
          questionClone.Title = titleValue;
        } else if (
          Object.prototype.hasOwnProperty.call(questionClone, "Prompt")
        ) {
          questionClone.Prompt = titleValue;
        } else if (
          Object.prototype.hasOwnProperty.call(questionClone, "Stem")
        ) {
          questionClone.Stem = titleValue;
        }
      }

      base.Question = questionClone;
    }

    if (!handledOptions) {
      base.Options = options;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "Title") ||
      !handledOptions
    ) {
      base.Title = titleValue;
    }

    if (points !== undefined) {
      base.points = points;
    } else if (Object.prototype.hasOwnProperty.call(base, "points")) {
      delete base.points;
    }

    if (Object.prototype.hasOwnProperty.call(base, "Name") || itemState?.Name) {
      base.Name = itemState?.Name || "";
    }

    if (Object.prototype.hasOwnProperty.call(base, "Type") || itemState?.Type) {
      base.Type = itemState?.Type || base.Type || "MCMA";
    }

    if (Object.prototype.hasOwnProperty.call(base, "skilltype")) {
      const effectiveSkilltype =
        normalizedSkilltype || normalizeSkillTypeValue(base.skilltype);

      base.skilltype = effectiveSkilltype;
    } else if (normalizedSkilltype) {
      base.skilltype = normalizedSkilltype;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "itemcat") ||
      itemState?.itemcat
    ) {
      base.itemcat = itemState?.itemcat || "";
    }

    return base;
  }

  const fallback = {
    Title: titleValue,
    Options: options,
  };

  if (points !== undefined) {
    fallback.points = points;
  }

  if (itemState?.Name) {
    fallback.Name = itemState.Name;
  }

  fallback.Type = itemState?.Type || "MCMA";

  if (normalizedSkilltype) {
    fallback.skilltype = normalizedSkilltype;
  }

  if (itemState?.itemcat) {
    fallback.itemcat = itemState.itemcat;
  }

  return fallback;
};

const extractItemIdentifier = (data) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  return data.id ?? data.Id ?? data.itemId ?? data.ItemId ?? undefined;
};

const MCMAItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isRouteNewItem = !itemId || itemId === "new";
  const location = useLocation();
  const clonedItemFromState = location.state?.clonedItem || null;
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [nextOptionId, setNextOptionId] = useState(1);
  const [effectiveItemId, setEffectiveItemId] = useState(
    clonedItemFromState ? null : isRouteNewItem ? null : itemId
  );
  const [jsonTemplate, setJsonTemplate] = useState(createDefaultJsonTemplate());
  const [jsonKeys, setJsonKeys] = useState(["Json"]);
  const [jsonSerializeMap, setJsonSerializeMap] = useState({ Json: false });
  const [lastSaveMode, setLastSaveMode] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isExistingItem = !clonedItemFromState && effectiveItemId;

  const canPreviewOrViewJson = Boolean(
    item &&
      item.Title &&
      Array.isArray(item.Options) &&
      item.Options.length >= DEFAULT_OPTION_COUNT
  );

  useEffect(() => {
    // If cloned → NEVER set effectiveItemId from URL
    if (clonedItemFromState) return;

    if (isRouteNewItem) {
      setEffectiveItemId(null);
    } else {
      setEffectiveItemId(itemId || null);
    }
  }, [isRouteNewItem, itemId, clonedItemFromState]);

  useEffect(() => {
    if (!ItemService.isConfigured()) {
      setError("Items API URL is not configured.");

      if (isRouteNewItem) {
        setItem(createEmptyItem());
        setJsonTemplate(createDefaultJsonTemplate());
        setJsonKeys(["Json"]);
        setJsonSerializeMap({ Json: false });
      } else {
        setItem(null);
      }

      return;
    }

    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setSaveError(null);
      setSaveSuccess(false);

      try {
        const [itemResponse, taxonomyResponse] = await Promise.all([
          clonedItemFromState
            ? Promise.resolve({ item: clonedItemFromState }) // ⬅ cloned
            : isRouteNewItem
            ? Promise.resolve(null)
            : ItemService.fetchItemById(itemId),
          ItemService.fetchTaxonomy("arabic"),
        ]);
        if (!isMounted) {
          return;
        }

        let resolvedItem = isRouteNewItem
          ? createEmptyItem()
          : resolveItemFromResponse(itemResponse);

        // 🔥 If this is a cloned item → force create mode
        if (clonedItemFromState) {
          resolvedItem = {
            ...clonedItemFromState,
            id: undefined,
            Id: undefined,
            itemId: undefined,
            ItemId: undefined,
            dateCreated: new Date().toISOString(),
            dateUpdated: new Date().toISOString(),
            json: undefined,
            Json: undefined,
            version: "v1",
          };

          setEffectiveItemId(null); // 🔥 force create
        }

        if (!resolvedItem) {
          throw new Error("Unable to locate the requested item.");
        }

        const options = Array.isArray(resolvedItem.Options)
          ? resolvedItem.Options
          : [];
        const highestId = options.reduce((max, option, index) => {
          const candidate = Number(option?.id ?? index + 1) || 0;
          return candidate > max ? candidate : max;
        }, 0);
        const nextId =
          highestId > 0 ? highestId + 1 : (options.length || 0) + 1;

        setNextOptionId(nextId);
        setItem({
          ...resolvedItem,
          Type: resolvedItem.Type || "MCMA",
          Options: options,
          skilltype: normalizeSkillTypeValue(resolvedItem.skilltype),
        });

        const jsonState = parseJsonTemplate(resolvedItem);

        if (jsonState.keys.length) {
          setJsonTemplate(jsonState.template || createDefaultJsonTemplate());
          setJsonKeys(jsonState.keys);
          setJsonSerializeMap(jsonState.serializeMap);
        } else {
          setJsonTemplate(createDefaultJsonTemplate());
          setJsonKeys(["Json"]);
          setJsonSerializeMap({ Json: false });
        }

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        const identifier = extractItemIdentifier(resolvedItem);
        // Do NOT set ID for cloned items → always create new
        if (!clonedItemFromState) {
          setEffectiveItemId(
            isRouteNewItem ? null : identifier ?? itemId ?? null
          );
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        if (isRouteNewItem) {
          setItem(createEmptyItem());
          setJsonTemplate(createDefaultJsonTemplate());
          setJsonKeys(["Json"]);
          setJsonSerializeMap({ Json: false });
          setTaxonomyOptions([]);
        } else {
          setItem(null);
        }

        setError(
          err?.response?.data?.message || err?.message || "Failed to load item."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [itemId, isRouteNewItem]);

  const handleCloneFromEditor = useCallback(() => {
    if (!item) return;

    const clonedPayload = {
      ...item,

      // 🔥 CRITICAL: remove all identifiers
      id: undefined,
      Id: undefined,
      itemId: undefined,
      ItemId: undefined,

      // Metadata reset
      Name: `${item.Name || "Untitled"} (Copy)`,
      version: "v1",
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),

      // JSON must be rebuilt on save
      json: undefined,
      Json: undefined,
    };

    navigate("/authoring/items/mcma/item/new", {
      state: { clonedItem: clonedPayload },
    });
  }, [item, navigate]);

  const handleFieldChange = useCallback((field, value) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }
      setIsDirty(true);

      const nextValue =
        field === "skilltype" ? normalizeSkillTypeValue(value) : value;
      return { ...prev, [field]: nextValue };
    });
  }, []);

  const handleOptionChange = useCallback((index, field, value) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }
      setIsDirty(true);

      const options = Array.isArray(prev.Options) ? [...prev.Options] : [];

      if (!options[index]) {
        return prev;
      }

      options[index] = { ...options[index], [field]: value };

      return {
        ...prev,
        Options: options,
      };
    });
  }, []);

  const handleAddOption = useCallback(() => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }
      setIsDirty(true);

      const options = Array.isArray(prev.Options) ? [...prev.Options] : [];

      const newOption = {
        id: nextOptionId,
        Value: "",
        IsCorrect: false,
        Rationale: "",
      };

      return {
        ...prev,
        Options: [...options, newOption],
      };
    });

    setNextOptionId((current) => current + 1);
  }, [nextOptionId]);

  const handleRemoveOption = useCallback((index) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }
      setIsDirty(true);

      const options = Array.isArray(prev.Options) ? [...prev.Options] : [];

      options.splice(index, 1);

      return {
        ...prev,
        Options: options,
      };
    });
  }, []);

  const { isItemcatValid, itemcatWarningMessage } = useMemo(() => {
    if (loading) {
      return { isItemcatValid: false, itemcatWarningMessage: null };
    }

    if (!taxonomyOptions.length) {
      return {
        isItemcatValid: false,
        itemcatWarningMessage:
          "No taxonomy results are available. Please refresh the page to try again.",
      };
    }

    if (!item?.itemcat) {
      return { isItemcatValid: false, itemcatWarningMessage: null };
    }

    const matchesTaxonomy = taxonomyOptions.some(
      (option) => option.value === item.itemcat
    );

    if (!matchesTaxonomy) {
      return {
        isItemcatValid: false,
        itemcatWarningMessage: `The selected item category "${item.itemcat}" is not part of the taxonomy results.`,
      };
    }

    return { isItemcatValid: true, itemcatWarningMessage: null };
  }, [item?.itemcat, loading, taxonomyOptions]);

  const isNameValid = useMemo(() => {
    if (!item?.Name || typeof item.Name !== "string") {
      return false;
    }

    return item.Name.trim().length > 0;
  }, [item?.Name]);

  const isSkillTypeValid = useMemo(() => {
    if (!item?.skilltype || typeof item.skilltype !== "string") {
      return false;
    }

    return item.skilltype.trim().length > 0;
  }, [item?.skilltype]);

  const isPointsValid = useMemo(() => {
    if (
      item?.points === null ||
      item?.points === undefined ||
      item?.points === ""
    ) {
      return false;
    }

    const numericValue = Number(item.points);
    return Number.isFinite(numericValue);
  }, [item?.points]);

  const hasPromptContent = useMemo(() => {
    if (!item?.Title || typeof item.Title !== "string") {
      return false;
    }

    const textContent = item.Title.replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();

    return textContent.length > 0;
  }, [item?.Title]);

  const hasMinimumValidOptions = useMemo(() => {
    if (!Array.isArray(item?.Options)) {
      return false;
    }

    const validOptions = item.Options.filter((option) => {
      if (!option || typeof option.Value !== "string") {
        return false;
      }

      return option.Value.trim().length > 0;
    });

    return validOptions.length >= DEFAULT_OPTION_COUNT;
  }, [item?.Options]);

  const isCreateMode = !effectiveItemId;

  const isCreateFormValid = useMemo(() => {
    if (!isCreateMode) {
      return true;
    }

    return (
      isNameValid &&
      isSkillTypeValid &&
      isItemcatValid &&
      isPointsValid &&
      hasPromptContent &&
      hasMinimumValidOptions
    );
  }, [
    hasMinimumValidOptions,
    hasPromptContent,
    isCreateMode,
    isItemcatValid,
    isNameValid,
    isPointsValid,
    isSkillTypeValid,
  ]);

  const handleSave = useCallback(async () => {
    if (!item) return;

    if (!isItemcatValid) {
      setSaveSuccess(false);
      return;
    }

    if (!isCreateFormValid) {
      setSaveSuccess(false);
      setSaveError("Please complete all required fields before saving.");
      return;
    }

    const isCreateAction = !effectiveItemId;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setLastSaveMode(isCreateAction ? "create" : "update");
    setIsDirty(false);

    try {
      const normalizedOptions = normaliseOptionsList(item.Options);
      const normalizedPoints = normalisePointsValue(item.points);

      const payload = {
        ...item,
        Type: item.Type || "MCMA",
        Options: normalizedOptions,
        skilltype: normalizeSkillTypeValue(item.skilltype),
      };

      if (normalizedPoints !== undefined) payload.points = normalizedPoints;
      else delete payload.points;

      // 🔥 Build clean updated JSON template
      const updatedJson = applyItemToJsonTemplate(
        jsonTemplate,
        item,
        normalizedOptions,
        normalizedPoints
      );

      payload.json = JSON.stringify(updatedJson);
      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      const resolvedSavedItem = resolveItemFromResponse(response);
      const nextItem =
        resolvedSavedItem && typeof resolvedSavedItem === "object"
          ? resolvedSavedItem
          : { ...item, ...payload };

      const normalizedSavedOptions = Array.isArray(nextItem.Options)
        ? nextItem.Options
        : normalizedOptions;

      const nextHighestId = normalizedSavedOptions.reduce(
        (max, option, index) => {
          const candidate = Number(option?.id ?? index + 1) || 0;
          return candidate > max ? candidate : max;
        },
        0
      );

      const nextId =
        nextHighestId > 0
          ? nextHighestId + 1
          : (normalizedSavedOptions.length || 0) + 1;

      setNextOptionId(nextId);
      const mergedPoints =
        normalizedPoints !== undefined
          ? normalizedPoints
          : nextItem.points !== undefined
          ? nextItem.points
          : item.points;

      const mergedItemState = {
        ...nextItem,
        Type: nextItem.Type || "MCMA",
        Options: normalizedSavedOptions,
        points: mergedPoints === undefined ? "" : mergedPoints,
        skilltype: normalizeSkillTypeValue(
          nextItem.skilltype ?? item.skilltype
        ),
      };

      setItem(mergedItemState);

      // Update template state
      setJsonTemplate(updatedJson);

      const identifier =
        extractItemIdentifier(nextItem) ||
        extractItemIdentifier(resolvedSavedItem);

      // Only assign ID after save IF this is not a cloned item
      if (!effectiveItemId && identifier) {
        setEffectiveItemId(identifier);
      }

      setSaveSuccess(true);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || err?.message || "Unable to save item."
      );
    } finally {
      setSaving(false);
    }
  }, [effectiveItemId, isCreateFormValid, isItemcatValid, item, jsonTemplate]);

  const taxonomySelectOptions = useMemo(() => {
    const hasTaxonomyOptions = taxonomyOptions.length > 0;

    if (!hasTaxonomyOptions) {
      if (item?.itemcat) {
        return [{ value: item.itemcat, label: item.itemcat }];
      }

      return [];
    }

    if (!item?.itemcat) {
      return taxonomyOptions;
    }

    const matchesExistingOption = taxonomyOptions.some(
      (option) => option.value === item.itemcat
    );

    if (matchesExistingOption) {
      return taxonomyOptions;
    }

    return [{ value: item.itemcat, label: item.itemcat }, ...taxonomyOptions];
  }, [item?.itemcat, taxonomyOptions]);

  const skillSelectOptions = useMemo(() => {
    if (!item?.skilltype) {
      return BLOOMS_TAXONOMY_SKILLS;
    }

    const matchesBloomSkill = BLOOMS_TAXONOMY_SKILLS.some(
      (option) => option.value === item.skilltype
    );

    if (matchesBloomSkill) {
      return BLOOMS_TAXONOMY_SKILLS;
    }

    return [
      ...BLOOMS_TAXONOMY_SKILLS,
      { value: item.skilltype, label: item.skilltype },
    ];
  }, [item?.skilltype]);

  const actionButtonLabel = saving
    ? isCreateMode
      ? "Creating..."
      : "Saving..."
    : isCreateMode
    ? "Create"
    : "Save";

  const successMessage =
    lastSaveMode === "create"
      ? "Item created successfully."
      : "Item updated successfully.";

  const jsonPreviewData = useMemo(() => {
    if (!item) {
      return null;
    }

    const normalizedOptions = normaliseOptionsList(item.Options);
    const normalizedPoints = normalisePointsValue(item.points);
    const payload = {
      ...item,
      Type: item.Type || "MCMA",
      Options: normalizedOptions,
    };

    payload.skilltype = normalizeSkillTypeValue(item.skilltype);

    if (normalizedPoints !== undefined) {
      payload.points = normalizedPoints;
    } else {
      delete payload.points;
    }

    const updatedJson = applyItemToJsonTemplate(
      jsonTemplate,
      item,
      normalizedOptions,
      normalizedPoints
    );

    if (updatedJson && typeof updatedJson === "object") {
      const keysToUpdate = jsonKeys.length ? jsonKeys : ["Json"];
      const serializeMapForKeys = jsonKeys.length
        ? jsonSerializeMap
        : { Json: false };

      keysToUpdate.forEach((key) => {
        const shouldSerialize = serializeMapForKeys[key] ?? false;
        payload[key] = shouldSerialize
          ? JSON.stringify(updatedJson)
          : updatedJson;
      });

      if (!keysToUpdate.includes("Json")) {
        payload.Json = updatedJson;
      }

      if (!keysToUpdate.includes("json")) {
        delete payload.json;
      }
    }

    if ("Json" in payload && "json" in payload) {
      delete payload.json;
    }

    return payload;
  }, [item, jsonKeys, jsonSerializeMap, jsonTemplate]);

  const jsonPreviewString = useMemo(
    () => (jsonPreviewData ? JSON.stringify(jsonPreviewData, null, 2) : ""),
    [jsonPreviewData]
  );

  const handleOpenJsonDialog = useCallback(() => {
    setJsonDialogOpen(true);
  }, []);

  const handleCloseJsonDialog = useCallback(() => {
    setJsonDialogOpen(false);
  }, []);

  if (loading) {
    return (
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary">
            Loading item details...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
        </Stack>
      </Paper>
    );
  }

  if (!item) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={item.Type || "MCMA"}
                color="secondary"
                variant="filled"
              />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {item.Name || "Untitled MCMA item"}
              </Typography>
            </Stack>

            {/* ---- Buttons on the right ---- */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!isExistingItem}
                onClick={handleCloneFromEditor}
              >
                Clone
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPreviewOpen(true)}
                disabled={!canPreviewOrViewJson}
              >
                Preview
              </Button>

              <Button
                variant="outlined"
                onClick={handleOpenJsonDialog}
                disabled={!canPreviewOrViewJson}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {item.version && (
            <Typography variant="body2" color="text.secondary">
              Version {item.version}
            </Typography>
          )}
          <Divider />

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              {successMessage}
            </Alert>
          )}

          {saveError && <Alert severity="error">{saveError}</Alert>}

          {itemcatWarningMessage && (
            <Alert severity="warning">{itemcatWarningMessage}</Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item name"
                fullWidth
                required
                value={item.Name || ""}
                onChange={(event) =>
                  handleFieldChange("Name", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                label="Points"
                fullWidth
                required
                value={item.points ?? ""}
                onChange={(event) => {
                  const { value } = event.target;
                  handleFieldChange(
                    "points",
                    value === "" ? "" : Number(value)
                  );
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel id="mcma-skilltype-label" required>
                  Skill type
                </InputLabel>
                <Select
                  labelId="mcma-skilltype-label"
                  label="Skill type"
                  value={item.skilltype || ""}
                  onChange={(event) =>
                    handleFieldChange("skilltype", event.target.value)
                  }
                >
                  <MenuItem value="" disabled>
                    <em>Not set</em>
                  </MenuItem>
                  {skillSelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(itemcatWarningMessage) && !loading}
              >
                <InputLabel id="mcma-itemcat-label" required>
                  Item category
                </InputLabel>
                <Select
                  labelId="mcma-itemcat-label"
                  label="Item category"
                  value={item.itemcat || ""}
                  onChange={(event) =>
                    handleFieldChange("itemcat", event.target.value)
                  }
                >
                  {taxonomySelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Instructions (Title)
            </Typography>
            <TinyEditor
              value={item.Title || ""}
              onChange={(value) => handleFieldChange("Title", value)}
              minHeight={250}
            />
          </Box>
        </Stack>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Answer options
              <Typography
                component="span"
                color="error"
                sx={{ ml: 0.5 }}
                aria-hidden="true"
              >
                *
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mark all correct answers and provide optional rationales.
            </Typography>
          </Box>

          <Stack spacing={2}>
            {Array.isArray(item.Options) && item.Options.length > 0 ? (
              item.Options.map((option, index) => (
                <Paper
                  key={option.id || index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: (theme) =>
                      theme.palette.grey?.[500] ||
                      theme.palette.grey?.[600] ||
                      theme.palette.grey?.main ||
                      theme.palette.divider,
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label={`Option ${index + 1}`}
                        fullWidth
                        value={option.Value || ""}
                        onChange={(event) =>
                          handleOptionChange(index, "Value", event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Rationale"
                        fullWidth
                        multiline
                        minRows={2}
                        value={option.Rationale || ""}
                        onChange={(event) =>
                          handleOptionChange(
                            index,
                            "Rationale",
                            event.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(option.IsCorrect)}
                            onChange={(event) =>
                              handleOptionChange(
                                index,
                                "IsCorrect",
                                event.target.checked
                              )
                            }
                          />
                        }
                        label="Correct answer"
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <IconButton
                        color="error"
                        aria-label="Remove option"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            ) : (
              <Alert severity="info">
                No options available. Add one to begin.
              </Alert>
            )}
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent={{ sm: "space-between" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ pt: 1 }}
          >
            <Button startIcon={<AddIcon />} onClick={handleAddOption}>
              Add option
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={isCreateMode ? <AddIcon /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !isItemcatValid || !isCreateFormValid}
              sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
            >
              {actionButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {/* --- PREVIEW DIALOG (always separate) --- */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>MCMA Preview</DialogTitle>
        <DialogContent dividers>
          <QuizPage
            previewMode
            previewActivities={[
              {
                ...item,
                ContentId: "preview",
              },
            ]}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* --- JSON DIALOG (completely separate) --- */}
      <Dialog
        open={jsonDialogOpen}
        onClose={handleCloseJsonDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON preview</DialogTitle>

        <DialogContent dividers>
          {jsonPreviewString ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
              }}
            >
              {jsonPreviewString}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Unable to generate item JSON preview.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseJsonDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MCMAItemDetail;
